"use client";

import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  useRef,
  Suspense,

  useMemo,

  useLayoutEffect,
} from "react";
import { DnaModel } from "./dnamodel";
import * as THREE from "three";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { Preload } from "@react-three/drei";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

function Scene() {
  const wrapperRef = useRef<THREE.Group>(null!);
  const dnaRef = useRef<THREE.Group>(null!);
  const target = useRef({
    position: new THREE.Vector3(0, 0, 0),
    rotationY: 0,
  });
  const scalteFactor = Math.min(Math.max(window.innerWidth / 768, 0.5), 1.7);
  const viewport = useThree((state) => state.viewport);
  const pointer = useMemo(() => ({ x: 0, y: 0 }), []);

  // Use useLayoutEffect for animation setup - runs synchronously after DOM mutations
  useLayoutEffect(() => {

    const handleMouseMove = (e: MouseEvent) => {
      pointer.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
  
    // Check if we're on mobile
    const isMobile = window.innerWidth < 768;

    // Desktop initial position
    target.current.position.set(0, 0, 0);
    target.current.rotationY = 0;

    // First animation: move to position for panel 1
    const positionTween = gsap.to(target.current.position, {
      x: isMobile ? 0 : Math.floor(-viewport.width * 0.5),
      y: isMobile ? 1 : 0, // Move slightly down on mobile
      z: isMobile ? 0 : -5,
      scrollTrigger: {
        trigger: "#panel-1",
        start: isMobile ? "top 80%" : "top 70%",
        end: isMobile ? "top 40%" : "top 20%",
        scrub: true,
      },
      onUpdate: () => ScrollTrigger.update(),
    });

    // Rotation animation for panel 1
    const rotationTween = gsap.to(target.current, {
      rotationY: isMobile ? Math.PI / 4 : Math.PI / 2,
      scrollTrigger: {
        trigger: "#panel-1",
        start: isMobile ? "top 80%" : "top 70%",
        end: isMobile ? "top 40%" : "top 20%",
        scrub: true,
      },
    });
    
    // For mobile, use a simple hide animation without the horizontal scrolling logic
    // let hideModelTween;
    // let spinExitTween;

    // hideModelTween = gsap.to(target.current.position, {
    //   x: -15, // Move far left (off screen)
    //   immediateRender: false,
    //   scrollTrigger: {
    //     trigger: "#panel-2",
    //     scroller: ".horizontal-scroll-container",
    //     horizontal: true,
    //     start: "left center",
    //     end: "left center",
    //     scrub: 0.5,
    //     containerAnimation: window.__catdev_scrollTween,
    //   },
    // });

    // spinExitTween = gsap.to(target.current, {
    //   rotationY: Math.PI * 2, // Full 360-degree spin
    //   scrollTrigger: {
    //     trigger: "#panel-2",
    //     scroller: ".horizontal-scroll-container",
    //     horizontal: true,
    //     start: "left right",
    //     end: "left center",
    //     scrub: 0.5,
    //   },
    // });

    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      // Kill all animations with null checks
      positionTween?.kill();
      rotationTween?.kill();
      // hideModelTween?.kill();
      // spinExitTween?.kill();
    };
  }, [pointer,viewport.width]);

  useFrame((_, delta) => {
    if (!dnaRef.current || !wrapperRef.current) return;

    // DNA itself keeps spinning in X
    dnaRef.current.rotation.x += delta * 0.2;

    //hover
    dnaRef.current.rotation.y = pointer.x * -0.02;
    dnaRef.current.rotation.z = pointer.y * -0.02;

    // Wrapper holds scroll-based Y rotation
    wrapperRef.current.rotation.x +=
      (target.current.rotationY - wrapperRef.current.rotation.y) * 0.05;

    wrapperRef.current.position.lerp(target.current.position, 0.05);
    wrapperRef.current.rotation.y +=
      (target.current.rotationY - wrapperRef.current.rotation.y) * 0.05;
  });

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.3} />
      <directionalLight position={[10, 10, 5]} intensity={1.5} castShadow />
      <directionalLight position={[-10, -10, -5]} intensity={0.5} />

      <Suspense fallback={null}>
        <group ref={wrapperRef} scale={scalteFactor}>
          <DnaModel ref={dnaRef} />
        </group>
      </Suspense>

      {/* Effects */}
      <EffectComposer>
        <Bloom
          intensity={1.5}
          luminanceThreshold={0.3}
          luminanceSmoothing={0.9}
        />
      </EffectComposer>
    </>
  );
}

// Updated Background3d component
function Background3d() {
 
  return (
    <div
      className="fixed inset-0 z-0 bg-[#020007]"
      style={{ width: "100%", height: "100%" }}
    >
      <Canvas>
        <color attach="background" args={["#020007"]} />
        <Scene />
        <Preload all />
      </Canvas>
    </div>
  );
}

export default Background3d;
